// Le récap des marges : le tableur de Romain, dans le CRM.
//
// En haut, l'année : un mois par ligne, calculé comme il faut (marge par jour
// et par heure PONDÉRÉES, pas des moyennes simples). En dessous, le mois
// choisi : une ligne par chantier, qui se modifie sur place — facture HT,
// achats (détail par fournisseur), jours, équipe, heures, précisions.
// Le calcul vit dans @imp/shared/recap-marges, pur et testé.

import { messageErreur } from '@imp/shared/message-erreur';
import {
  type AchatRecap,
  analyserRecapMarges,
  calculerLigneMarge,
  feuillesExportMarges,
  lignesDepuisVentes,
  type LigneReprise,
  nombreCellule,
  recapAnnuel,
  type TotalMois,
} from '@imp/shared/recap-marges';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Download, Plus, Trash2, Upload, X } from 'lucide-react';
import { Fragment, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatEuro } from '@/modules/catalogue/api';
import { ChoixChantier } from '@/modules/chantiers/ChoixChantier';
import { listChantiers } from '@/modules/chantiers/api';
import { listContacts } from '@/modules/clients/api';
import {
  type ChampsLigne,
  creerLigneMarge,
  creerLignesMarge,
  enregistrerAchats,
  exporterRecapMarges,
  type LigneMargeStockee,
  lireClasseurRecap,
  listLignesMarges,
  listVentesPourRecap,
  modifierLigneMarge,
  reprendreTableur,
  supprimerLigneMarge,
} from '@/modules/pilotage/marges-api';

const MOIS = [
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre',
];

const NOMBRE = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 });
const MONTANT = new Intl.NumberFormat('fr-FR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const eur0 = (n: number | null): string =>
  n === null
    ? ''
    : new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
      }).format(n);
const pct = (t: number | null): string =>
  t === null ? '' : `${NOMBRE.format(Math.round(t * 1000) / 10)} %`;
const ton = (n: number | null): string =>
  n === null ? '' : n < 0 ? 'text-prio-high' : 'text-money-in';

/** Un champ numérique qui s'enregistre en quittant la case. */
function CaseNombre({
  valeur,
  onEnregistrer,
  largeur = 'w-24',
  label,
  montant = false,
}: {
  valeur: number | null;
  onEnregistrer: (n: number | null) => void;
  largeur?: string;
  label: string;
  /** Un montant en euros : toujours deux décimales. */
  montant?: boolean;
}) {
  return (
    <input
      key={String(valeur)}
      aria-label={label}
      inputMode="decimal"
      defaultValue={valeur === null ? '' : (montant ? MONTANT : NOMBRE).format(valeur)}
      className={`${largeur} hover:border-input focus:border-ring rounded border border-transparent bg-transparent px-1 py-0.5 text-right tabular-nums focus:outline-none`}
      onBlur={(e) => {
        const n = nombreCellule(e.target.value);
        if (n !== valeur) onEnregistrer(n);
      }}
    />
  );
}

function LigneAnnee({
  t,
  actif,
  ouvert,
  onChoisir,
}: {
  t: TotalMois;
  actif: boolean;
  /** `null` : la ligne de l'année, qui ne s'ouvre pas. */
  ouvert: boolean | null;
  onChoisir: () => void;
}) {
  return (
    <tr
      className={`border-muted/60 border-b ${ouvert === null ? '' : 'hover:bg-muted/40 cursor-pointer'} ${
        actif ? 'bg-primary/5 font-semibold' : ''
      } ${t.lignes === 0 ? 'text-muted-foreground/60' : ''}`}
      onClick={onChoisir}
    >
      <td className="py-1 pr-2">
        <span className="flex items-center gap-1">
          {ouvert === null ? (
            <span className="w-3.5" />
          ) : ouvert ? (
            <ChevronDown className="text-muted-foreground h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="text-muted-foreground h-3.5 w-3.5" />
          )}
          {t.mois === 0 ? 'Année' : MOIS[t.mois - 1]}
        </span>
      </td>
      <td className="px-2 text-right tabular-nums">{t.lignes || ''}</td>
      <td className="px-2 text-right tabular-nums">{t.factureHt ? eur0(t.factureHt) : ''}</td>
      <td className="px-2 text-right tabular-nums">{t.achats ? eur0(t.achats) : ''}</td>
      <td
        className={`px-2 text-right font-semibold tabular-nums ${ton(t.lignes ? t.marge : null)}`}
      >
        {t.lignes && t.factureHt - t.factureSansAchats > 0 ? eur0(t.marge) : ''}
      </td>
      <td className="px-2 text-right tabular-nums">{pct(t.taux)}</td>
      <td className="px-2 text-right tabular-nums">{eur0(t.margeJour)}</td>
      <td className="px-2 text-right tabular-nums">{eur0(t.margeHeure)}</td>
      <td className="px-2 text-right tabular-nums">
        {t.lignesSansAchats > 0 ? (
          <span
            className="text-amber-700"
            title={`${eur0(t.factureSansAchats)} facturés sans achats connus : hors marge.`}
          >
            {t.lignesSansAchats}
          </span>
        ) : (
          ''
        )}
      </td>
    </tr>
  );
}

/** Le détail des achats d'une ligne : fournisseur par fournisseur. */
function EditeurAchats({ ligne, onFermer }: { ligne: LigneMargeStockee; onFermer: () => void }) {
  const qc = useQueryClient();
  // On ouvre l'éditeur pour saisir : les champs s'affichent d'emblée.
  const [connus, setConnus] = useState(true);
  const [lignes, setLignes] = useState<{ fournisseur: string; montant: string }[]>(
    ligne.achats.length > 0
      ? ligne.achats.map((a) => ({ fournisseur: a.fournisseur, montant: NOMBRE.format(a.montant) }))
      : [{ fournisseur: '', montant: '' }],
  );
  const lus: AchatRecap[] = lignes
    .map((l) => ({ fournisseur: l.fournisseur, montant: nombreCellule(l.montant) }))
    .filter((a): a is AchatRecap => a.montant !== null);
  const total = lus.reduce((t, a) => t + a.montant, 0);
  const enregistrer = useMutation({
    mutationFn: () => enregistrerAchats(ligne.id, connus, lus),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['marges'] });
      onFermer();
    },
  });
  return (
    <div className="bg-card space-y-2 rounded-md border p-3 text-sm">
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium">Achats — {ligne.libelle || 'ligne'}</p>
        <button type="button" aria-label="Fermer" onClick={onFermer} className="p-1">
          <X className="h-4 w-4" />
        </button>
      </div>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={!connus}
          onChange={(e) => {
            setConnus(!e.target.checked);
          }}
        />
        Achats pas encore connus — la ligne reste hors de la marge
      </label>
      {connus ? (
        <>
          {lignes.map((l, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                aria-label="Fournisseur"
                placeholder="Fournisseur (POINT P, SIMC…)"
                className="h-8 w-56"
                value={l.fournisseur}
                onChange={(e) => {
                  setLignes(
                    lignes.map((x, k) => (k === i ? { ...x, fournisseur: e.target.value } : x)),
                  );
                }}
              />
              <Input
                aria-label="Montant HT"
                placeholder="Montant HT"
                inputMode="decimal"
                className="h-8 w-32 text-right"
                value={l.montant}
                onChange={(e) => {
                  setLignes(
                    lignes.map((x, k) => (k === i ? { ...x, montant: e.target.value } : x)),
                  );
                }}
              />
              <button
                type="button"
                aria-label="Retirer cet achat"
                className="text-muted-foreground hover:text-destructive p-1"
                onClick={() => {
                  setLignes(lignes.filter((_, k) => k !== i));
                }}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setLignes([...lignes, { fournisseur: '', montant: '' }]);
            }}
          >
            <Plus className="h-4 w-4" /> Un achat
          </Button>
          <p className="tabular-nums">
            Total : <strong>{formatEuro(total)}</strong> HT
          </p>
          {lus.length === 0 ? (
            <p className="text-muted-foreground text-xs">
              Saisissez au moins un montant — 0 s’il n’y a eu aucun achat sur ce chantier.
            </p>
          ) : null}
        </>
      ) : null}
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          disabled={enregistrer.isPending || (connus && lus.length === 0)}
          onClick={() => {
            enregistrer.mutate();
          }}
        >
          Enregistrer les achats
        </Button>
        {enregistrer.isError ? (
          <span className="text-destructive">{messageErreur(enregistrer.error)}</span>
        ) : null}
      </div>
    </div>
  );
}

export function RecapMargesPanel({ annee }: { annee: number }) {
  const qc = useQueryClient();
  const lignesQ = useQuery({ queryKey: ['marges', annee], queryFn: () => listLignesMarges(annee) });
  const chantiersQ = useQuery({ queryKey: ['chantiers'], queryFn: listChantiers });
  const contactsQ = useQuery({ queryKey: ['contacts'], queryFn: listContacts });
  const lignes = lignesQ.data ?? [];
  const recap = recapAnnuel(lignes);
  // Un clic sur un mois l'ouvre juste en dessous, avec tous ses chantiers.
  const [moisOuvert, setMoisOuvert] = useState<number | null>(null);
  const mois = moisOuvert ?? 1;
  const duMois = lignes.filter((l) => l.mois === mois);
  // Les ventes du mois : pour vérifier que le récap n'oublie aucun chantier, et
  // pré-remplir un mois encore vide.
  const ventesQ = useQuery({
    queryKey: ['marges-ventes', annee],
    queryFn: () => listVentesPourRecap(annee),
  });
  const ventesDuMois = ventesQ.data?.[mois - 1] ?? [];
  const preRemplies = lignesDepuisVentes(ventesDuMois);
  const totalVentes = ventesDuMois.reduce(
    (t, v) => t + (v.type === 'avoir' ? -Math.abs(v.ht) : v.ht),
    0,
  );
  const totalRecap = duMois.reduce((t, l) => t + (l.factureHt ?? 0), 0);
  const ecartVentes = Math.round((totalVentes - totalRecap) * 100) / 100;
  const [achatsOuverts, setAchatsOuverts] = useState<string | null>(null);
  const [relierOuvert, setRelierOuvert] = useState<string | null>(null);
  const nomChantier = (id: string | null): string | null => {
    if (id === null) return null;
    const ch = (chantiersQ.data ?? []).find((x) => x.id === id);
    return ch === undefined ? 'chantier' : `${ch.numero} — ${ch.libelle}`;
  };

  const rafraichir = (): void => {
    void qc.invalidateQueries({ queryKey: ['marges', annee] });
  };
  const modifier = useMutation({
    mutationFn: ({ id, champs }: { id: string; champs: ChampsLigne }) =>
      modifierLigneMarge(id, champs),
    onSuccess: rafraichir,
  });
  const ajouter = useMutation({
    mutationFn: () =>
      creerLigneMarge(annee, mois, duMois.reduce((m, l) => Math.max(m, l.ordre), 0) + 10),
    onSuccess: rafraichir,
  });
  const supprimer = useMutation({ mutationFn: supprimerLigneMarge, onSuccess: rafraichir });
  const exporter = useMutation({
    mutationFn: () =>
      exporterRecapMarges(
        annee,
        feuillesExportMarges(lignes, (id) => {
          const ch = (chantiersQ.data ?? []).find((x) => x.id === id);
          return ch === undefined ? null : `${ch.numero} — ${ch.libelle}`;
        }),
      ),
  });
  const preRemplir = useMutation({
    mutationFn: () => creerLignesMarge(annee, mois, 10, preRemplies),
    onSuccess: rafraichir,
  });

  // ----- Reprise du tableur -------------------------------------------------
  const fichierRef = useRef<HTMLInputElement>(null);
  const [reprise, setReprise] = useState<{
    nom: string;
    lignes: LigneReprise[];
    ignorees: string[];
  } | null>(null);
  const [remplacer, setRemplacer] = useState(true);
  const [erreurLecture, setErreurLecture] = useState<string | null>(null);
  const dejaRepris = lignes.filter((l) => l.source === 'excel').length;
  const reprendre = useMutation({
    mutationFn: (r: { lignes: LigneReprise[] }) => reprendreTableur(annee, r.lignes, remplacer),
    onSuccess: () => {
      setReprise(null);
      rafraichir();
    },
  });

  const champ = (id: string, champs: ChampsLigne): void => {
    modifier.mutate({ id, champs });
  };
  const erreur = modifier.error ?? ajouter.error ?? supprimer.error;

  const detailMois = (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-base font-semibold">
              {MOIS[mois - 1]} {annee} — {duMois.length} chantier{duMois.length > 1 ? 's' : ''}
            </h3>
            <Button
              size="sm"
              variant="outline"
              disabled={ajouter.isPending}
              onClick={() => {
                ajouter.mutate();
              }}
            >
              <Plus className="h-4 w-4" /> Une ligne
            </Button>
          </div>
          {ventesQ.data ? (
            <div
              className={`flex flex-wrap items-center gap-3 rounded-md border px-3 py-2 text-sm ${
                Math.abs(ecartVentes) >= 1 ? 'border-amber-200 bg-amber-50' : 'bg-muted/30'
              }`}
            >
              <span>
                Ventes du mois : <strong className="tabular-nums">{formatEuro(totalVentes)}</strong>{' '}
                HT · dans le récap :{' '}
                <strong className="tabular-nums">{formatEuro(totalRecap)}</strong>
                {Math.abs(ecartVentes) >= 1 ? (
                  <span className="text-amber-800">
                    {' '}
                    —{' '}
                    {totalVentes === 0
                      ? 'aucune vente de ce mois dans l’onglet Ventes : importez-les pour comparer.'
                      : ecartVentes > 0
                        ? `${formatEuro(ecartVentes)} vendus ce mois ne sont pas dans le récap : un chantier manque peut-être.`
                        : `le récap compte ${formatEuro(-ecartVentes)} de plus que les ventes du mois : une vente manque peut-être dans l’onglet Ventes.`}
                  </span>
                ) : null}
              </span>
              {duMois.length === 0 && preRemplies.length > 0 ? (
                <Button
                  size="sm"
                  disabled={preRemplir.isPending}
                  onClick={() => {
                    preRemplir.mutate();
                  }}
                >
                  Ajouter les {preRemplies.length} ventes du mois
                </Button>
              ) : null}
              {preRemplir.isError ? (
                <span className="text-destructive">{messageErreur(preRemplir.error)}</span>
              ) : null}
            </div>
          ) : null}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] border-collapse text-[13px]">
              <thead>
                <tr className="text-muted-foreground border-b text-left text-[10px] uppercase">
                  <th className="py-1 pr-2 font-medium">Chantier</th>
                  <th className="px-1 py-1 text-right font-medium">Facture HT</th>
                  <th className="px-1 py-1 text-right font-medium">Achats</th>
                  <th className="px-1 py-1 text-right font-medium">Marge</th>
                  <th className="px-1 py-1 text-right font-medium">Jours</th>
                  <th className="px-1 py-1 text-right font-medium">M / jour</th>
                  <th className="px-1 py-1 text-right font-medium">Éq.</th>
                  <th className="px-1 py-1 text-right font-medium">H / jour</th>
                  <th className="px-1 py-1 text-right font-medium">M / heure</th>
                  <th className="px-1 py-1 font-medium">Précisions</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {duMois.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="text-muted-foreground py-3">
                      {lignesQ.isLoading ? 'Chargement…' : 'Aucune ligne pour ce mois.'}
                    </td>
                  </tr>
                ) : null}
                {duMois.map((l) => {
                  const c = calculerLigneMarge(l);
                  return (
                    <Fragment key={l.id}>
                      <tr className="border-muted/50 border-b align-top">
                        <td className="min-w-[240px] py-1 pr-2">
                          <input
                            key={l.libelle}
                            aria-label="Nom de la ligne"
                            defaultValue={l.libelle}
                            placeholder="Nom (client)"
                            className="hover:border-input focus:border-ring w-full rounded border border-transparent bg-transparent px-1 py-0.5 font-medium focus:outline-none"
                            onBlur={(e) => {
                              if (e.target.value.trim() !== l.libelle)
                                champ(l.id, { libelle: e.target.value.trim() });
                            }}
                          />
                          {relierOuvert === l.id ? (
                            <div className="mt-1">
                              <ChoixChantier
                                chantiers={chantiersQ.data ?? []}
                                clients={contactsQ.data ?? []}
                                chargement={chantiersQ.isLoading}
                                value={l.chantierId ?? ''}
                                labelAucun="— pas de chantier relié —"
                                placeholder="Chercher le chantier"
                                onChange={(id) => {
                                  champ(l.id, { chantier_id: id === '' ? null : id });
                                  setRelierOuvert(null);
                                }}
                              />
                            </div>
                          ) : (
                            <button
                              type="button"
                              className={`block px-1 text-left text-xs hover:underline ${
                                l.chantierId === null ? 'text-muted-foreground' : 'text-primary'
                              }`}
                              onClick={() => {
                                setRelierOuvert(l.id);
                              }}
                            >
                              {nomChantier(l.chantierId) ?? 'Relier à un chantier'}
                            </button>
                          )}
                        </td>
                        <td className="px-1 text-right">
                          <CaseNombre
                            label="Facture HT"
                            montant
                            largeur="w-28"
                            valeur={l.factureHt}
                            onEnregistrer={(n) => {
                              champ(l.id, { facture_ht: n });
                            }}
                          />
                        </td>
                        <td className="px-1 text-right">
                          <button
                            type="button"
                            className={`hover:border-input rounded border border-transparent px-1 py-0.5 tabular-nums ${
                              c.achats === null ? 'text-amber-700' : ''
                            }`}
                            title={
                              c.achats === null
                                ? 'Achats pas encore connus : cliquez pour les saisir'
                                : l.achats
                                    .map((a) => `${a.fournisseur || '?'} ${formatEuro(a.montant)}`)
                                    .join(' + ')
                            }
                            onClick={() => {
                              setAchatsOuverts(achatsOuverts === l.id ? null : l.id);
                            }}
                          >
                            {c.achats === null ? 'à saisir' : formatEuro(c.achats)}
                          </button>
                        </td>
                        <td className={`px-1 text-right font-semibold tabular-nums ${ton(c.marge)}`}>
                          {c.marge === null ? '' : formatEuro(c.marge)}
                        </td>
                        <td className="px-1 text-right">
                          <CaseNombre
                            label="Jours"
                            largeur="w-14"
                            valeur={l.jours}
                            onEnregistrer={(n) => {
                              champ(l.id, { jours: n });
                            }}
                          />
                        </td>
                        <td className={`px-1 text-right tabular-nums ${ton(c.margeJour)}`}>
                          {eur0(c.margeJour)}
                        </td>
                        <td className="px-1 text-right">
                          <CaseNombre
                            label="Équipe"
                            largeur="w-12"
                            valeur={l.equipe}
                            onEnregistrer={(n) => {
                              champ(l.id, { equipe: n });
                            }}
                          />
                        </td>
                        <td className="px-1 text-right">
                          <CaseNombre
                            label="Heures par jour"
                            largeur="w-12"
                            valeur={l.heuresJour}
                            onEnregistrer={(n) => {
                              champ(l.id, { heures_jour: n });
                            }}
                          />
                        </td>
                        <td className={`px-1 text-right tabular-nums ${ton(c.margeHeure)}`}>
                          {eur0(c.margeHeure)}
                        </td>
                        <td className="px-1">
                          <input
                            key={l.precisions}
                            aria-label="Précisions"
                            defaultValue={l.precisions}
                            className="hover:border-input focus:border-ring w-full min-w-[160px] rounded border border-transparent bg-transparent px-1 py-0.5 text-xs focus:outline-none"
                            onBlur={(e) => {
                              if (e.target.value !== l.precisions)
                                champ(l.id, { precisions: e.target.value });
                            }}
                          />
                        </td>
                        <td className="text-right">
                          <button
                            type="button"
                            aria-label={`Retirer la ligne ${l.libelle}`}
                            className="text-muted-foreground hover:text-destructive p-1"
                            onClick={() => {
                              supprimer.mutate(l.id);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                      {achatsOuverts === l.id ? (
                        <tr>
                          <td colSpan={11} className="bg-muted/30 px-2 py-2">
                            <EditeurAchats
                              ligne={l}
                              onFermer={() => {
                                setAchatsOuverts(null);
                              }}
                            />
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {erreur ? <p className="text-destructive text-sm">{messageErreur(erreur)}</p> : null}
        </div>
  );

  return (
    <div className="space-y-5">
      {/* ----- Reprendre le tableur ----- */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          ref={fichierRef}
          type="file"
          accept=".xlsx,.xls"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (!f) return;
            setErreurLecture(null);
            lireClasseurRecap(f)
              .then((feuilles) => {
                const r = analyserRecapMarges(feuilles);
                setReprise({ nom: f.name, lignes: r.lignes, ignorees: r.feuillesIgnorees });
              })
              .catch((err: unknown) => {
                setErreurLecture(messageErreur(err));
              });
          }}
        />
        <Button variant="outline" size="sm" onClick={() => fichierRef.current?.click()}>
          <Upload className="h-4 w-4" /> Reprendre un récap Excel
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={lignes.length === 0 || exporter.isPending}
          title="Un fichier Excel au format de votre tableur : un onglet par mois, une ligne par chantier, et le récap annuel. Il se reprend tel quel."
          onClick={() => {
            exporter.mutate();
          }}
        >
          <Download className="h-4 w-4" /> Exporter les marges (Excel)
        </Button>
        {exporter.isError ? (
          <span className="text-destructive text-sm">{messageErreur(exporter.error)}</span>
        ) : null}
        {erreurLecture ? <span className="text-destructive text-sm">{erreurLecture}</span> : null}
      </div>

      {reprise !== null ? (
        <div className="space-y-2 rounded-md border border-sky-200 bg-sky-50 p-3 text-sm text-sky-950">
          <p className="font-semibold">
            {reprise.nom} : {reprise.lignes.length} ligne{reprise.lignes.length > 1 ? 's' : ''} à
            reprendre dans {annee}
          </p>
          <ul className="list-disc space-y-0.5 pl-5">
            {recapAnnuel(
              reprise.lignes.map((l, i) => ({
                id: String(i),
                mois: l.mois,
                libelle: l.libelle,
                chantierId: null,
                factureHt: l.factureHt,
                jours: l.jours,
                equipe: l.equipe,
                heuresJour: l.heuresJour,
                precisions: l.precisions,
                achatsConnus: l.achats !== null,
                achats: (l.achats ?? []).map((montant) => ({ fournisseur: '', montant })),
              })),
            )
              .mois.filter((m) => m.lignes > 0)
              .map((m) => (
                <li key={m.mois}>
                  {MOIS[m.mois - 1]} : {m.lignes} ligne{m.lignes > 1 ? 's' : ''},{' '}
                  {eur0(m.factureHt)} facturés
                  {m.lignesSansAchats > 0
                    ? `, dont ${String(m.lignesSansAchats)} sans achats connus`
                    : ''}
                </li>
              ))}
          </ul>
          {reprise.lignes.some((l) => l.achatsUnEuro) ? (
            <p>
              <strong>{reprise.lignes.filter((l) => l.achatsUnEuro).length}</strong> lignes avaient
              des achats à 1 € : reprises comme « achats pas encore connus », pour ne pas afficher
              une marge presque égale à la facture.
            </p>
          ) : null}
          <p className="text-xs">
            Les marges, marges par jour et par heure et les totaux du tableur ne sont pas repris :
            le CRM les recalcule.
            {reprise.ignorees.length > 0
              ? ` Feuilles ignorées : ${reprise.ignorees.join(', ')}.`
              : ''}
          </p>
          {dejaRepris > 0 ? (
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={remplacer}
                onChange={(e) => {
                  setRemplacer(e.target.checked);
                }}
              />
              Remplacer les {dejaRepris} lignes de {annee} déjà reprises d’un tableur — avec ce qui
              y a été complété ici depuis. Les lignes créées dans le CRM restent.
            </label>
          ) : null}
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              disabled={reprendre.isPending || reprise.lignes.length === 0}
              onClick={() => {
                reprendre.mutate({ lignes: reprise.lignes });
              }}
            >
              {reprendre.isPending
                ? 'Reprise…'
                : `Reprendre ${String(reprise.lignes.length)} lignes`}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setReprise(null);
              }}
            >
              Annuler
            </Button>
            {reprendre.isError ? (
              <span className="text-destructive">{messageErreur(reprendre.error)}</span>
            ) : null}
          </div>
        </div>
      ) : null}

      {lignesQ.isError ? (
        <p className="text-destructive text-sm">{messageErreur(lignesQ.error)}</p>
      ) : null}

      {/* ----- L'année ----- */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[820px] border-collapse text-sm">
          <thead>
            <tr className="text-muted-foreground border-b text-left text-[11px] uppercase">
              <th className="py-1.5 pr-2 font-medium">Mois</th>
              <th className="px-2 py-1.5 text-right font-medium">Lignes</th>
              <th className="px-2 py-1.5 text-right font-medium">Facture HT</th>
              <th className="px-2 py-1.5 text-right font-medium">Achats</th>
              <th className="px-2 py-1.5 text-right font-medium">Marge</th>
              <th className="px-2 py-1.5 text-right font-medium">Taux</th>
              <th
                className="px-2 py-1.5 text-right font-medium"
                title="Marge totale ÷ jours totaux"
              >
                M / jour
              </th>
              <th
                className="px-2 py-1.5 text-right font-medium"
                title="Marge totale ÷ heures travaillées (jours × équipe × heures)"
              >
                M / heure
              </th>
              <th
                className="px-2 py-1.5 text-right font-medium"
                title="Lignes dont les achats ne sont pas encore connus : hors marge"
              >
                Sans achats
              </th>
            </tr>
          </thead>
          <tbody>
            {recap.mois.map((t) => (
              <Fragment key={t.mois}>
                <LigneAnnee
                  t={t}
                  actif={t.mois === moisOuvert}
                  ouvert={t.mois === moisOuvert}
                  onChoisir={() => {
                    setMoisOuvert(t.mois === moisOuvert ? null : t.mois);
                  }}
                />
                {t.mois === moisOuvert ? (
                  <tr>
                    <td colSpan={9} className="bg-muted/20 px-2 pb-4 pt-2">
                      {detailMois}
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            ))}
            <tr className="border-t-2">
              <td colSpan={9} className="p-0" />
            </tr>
            <LigneAnnee t={recap.annee} actif={false} ouvert={null} onChoisir={() => undefined} />
          </tbody>
        </table>
        <p className="text-muted-foreground mt-1 text-xs">
          Marge, taux, marge par jour et par heure : sur les seules lignes dont les achats sont
          connus. Par jour et par heure, c’est la marge totale divisée par le temps total — pas une
          moyenne des lignes, qu’une petite ligne à perte suffisait à fausser.
        </p>
      </div>

    </div>
  );
}
